import { SIDEBAR_RAIL } from '../constants/appShell.constants';

/** Shared easing/duration for sidebar width + label animations (MUI mini-drawer pattern). */
export function createSidebarWidthTransition(theme) {
  return theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  });
}

export function createSidebarLabelSx(theme, collapsed) {
  const duration = collapsed
    ? theme.transitions.duration.shorter
    : theme.transitions.duration.standard;

  return {
    minWidth: 0,
    flex: collapsed ? '0 0 auto' : '1 1 auto',
    opacity: collapsed ? 0 : 1,
    maxWidth: collapsed ? 0 : 220,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    pointerEvents: collapsed ? 'none' : 'auto',
    ...(collapsed && {
      position: 'absolute',
      width: 0,
      height: 0,
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      clipPath: 'inset(50%)',
    }),
    transition: theme.transitions.create(['opacity', 'max-width', 'flex-basis'], {
      easing: theme.transitions.easing.easeInOut,
      duration,
      delay: collapsed ? 0 : 100,
    }),
  };
}

export function createSidebarIconBoxSx(theme, collapsed) {
  return {
    width: 36,
    height: 36,
    transition: theme.transitions.create(['width', 'height'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  };
}

export function createSidebarNavItemLayoutSx(theme, collapsed, depth = 0) {
  const transition = theme.transitions.create(['padding', 'margin', 'gap', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  });

  if (collapsed) {
    return {
      width: SIDEBAR_RAIL.itemSize,
      height: SIDEBAR_RAIL.itemSize,
      px: 0,
      mx: 'auto',
      justifyContent: 'center',
      gap: 0,
      transition,
    };
  }

  return {
    width: 'calc(100% - 24px)',
    px: 1.25 + depth * 1,
    mx: 1.5,
    justifyContent: 'flex-start',
    gap: 1.5,
    transition,
  };
}

/** Centers a fixed-size rail item inside the collapsed sidebar width. */
export function createSidebarRailItemWrapSx() {
  return {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    mb: 0.25,
  };
}

export function createSidebarBrandLayoutSx(theme, collapsed) {
  return {
    px: collapsed ? 0 : 2,
    justifyContent: collapsed ? 'center' : 'flex-start',
    gap: collapsed ? 0 : 1.25,
    position: 'relative',
    width: '100%',
    transition: theme.transitions.create(['padding', 'gap'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  };
}

export function createSidebarCollapseToggleSx(theme, collapsed, sidebar, shadows) {
  return {
    transition: theme.transitions.create(['border-color', 'box-shadow'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      bgcolor: sidebar.toggleBg,
      borderColor: 'rgba(0,0,0,0.1)',
      boxShadow: shadows.md,
    },
    '& .sidebar-toggle-icon': {
      transition: theme.transitions.create('transform', {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.standard,
      }),
    },
  };
}

/** Apex-style nav surface — gray pill active (expanded), green icon active (collapsed). */
export function createSidebarNavButtonSx(sidebar, { active, disabled, collapsed }) {
  const radius = sidebar.navRadius ?? 8;
  const activeColor = collapsed ? sidebar.accent : sidebar.text;

  return {
    border: 'none',
    background: active ? sidebar.active : 'transparent',
    color: active ? activeColor : sidebar.textNav,
    cursor: disabled ? 'default' : 'pointer',
    position: 'relative',
    opacity: disabled ? 0.45 : 1,
    borderRadius: `${radius}px`,
    '&:hover': disabled
      ? {}
      : {
          bgcolor: active ? sidebar.active : sidebar.hover,
          color: active ? activeColor : sidebar.text,
        },
  };
}

export function createSidebarNavIconSx() {
  return {
    fontSize: SIDEBAR_RAIL.iconSize,
    flexShrink: 0,
    color: 'inherit',
  };
}

export function createSidebarIconWrapSx() {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: SIDEBAR_RAIL.iconSize,
    height: SIDEBAR_RAIL.iconSize,
  };
}
